/**
 * What `/my_work` renders. Structurally typed so this module never imports server code.
 */

export const MY_WORK_BUCKETS = [
  'past',
  'today',
  'this_week',
  'next_week',
  'later',
  'no_date',
  'done',
] as const;
export type MyWorkBucketId = (typeof MY_WORK_BUCKETS)[number];

/** monday's labels, verbatim — note the capital **D** in `Past Dates` (`[mw] 01-anatomy.json`). */
export const MY_WORK_BUCKET_LABELS: Record<MyWorkBucketId, string> = {
  past: 'Past Dates',
  today: 'Today',
  this_week: 'This week',
  next_week: 'Next week',
  later: 'Later',
  no_date: 'Without a date',
  done: 'Done',
};

export const MY_WORK_GROUP_BY = ['date', 'board', 'status', 'priority'] as const;
export type MyWorkGroupById = (typeof MY_WORK_GROUP_BY)[number];

/**
 * The `Date view` switch. The four labels below the first are **ASSUMED**: the menu was
 * photographed at 200 × 160 with five 32 px rows whose strings were scrubbed to their lengths
 * (11 / 13 / 10 / 16), and the run that could read them could not be reproduced. Open question.
 */
export const MY_WORK_GROUP_BY_LABELS: Record<MyWorkGroupById, string> = {
  date: 'Date view',
  status: 'Status view',
  priority: 'Priority view',
  board: 'Board view',
};

export interface MyWorkCell {
  text: string;
  color: string | null;
  done?: boolean;
}

export interface MyWorkRowView {
  itemId: string;
  name: string;
  boardId: string;
  boardName: string;
  groupId: string;
  groupTitle: string;
  groupColor: string | null;
  date: string | null;
  dateColumnId: string | null;
  status: MyWorkCell | null;
  priority: MyWorkCell | null;
  people: { id: string; name: string | null; photoUrl: string | null }[];
  updateCount: number;
  unreadUpdateCount: number;
}

export interface MyWorkSectionView {
  key: string;
  bucket: MyWorkBucketId | null;
  label: string;
  count: number;
  rows: MyWorkRowView[];
}

export interface MyWorkSettingsView {
  groupBy: MyWorkGroupById;
  hideDone: boolean;
  boardIds: string[] | null;
  columnRoles: Record<string, { date?: string; status?: string; priority?: string; people?: string }>;
  sort: 'date_asc' | 'date_desc';
}

export interface MyWorkResultView {
  sections: MyWorkSectionView[];
  boards: { id: string; name: string; count: number }[];
  settings: MyWorkSettingsView;
}

export interface MyWorkListInput {
  groupBy?: MyWorkGroupById;
  hideDone?: boolean;
  query?: string;
  sort?: 'date_asc' | 'date_desc';
  boardIds?: number[];
}

export interface MyWorkSource {
  list(input: MyWorkListInput): Promise<MyWorkResultView>;
  settings(): Promise<MyWorkSettingsView>;
  saveSettings(patch: Partial<MyWorkSettingsView>): Promise<MyWorkSettingsView>;
}
